import {
  type AdjustmentMode,
  getDefaultAdjustmentModeFromSpecType,
  QualityFormulaType,
  type SpecType,
  TierMode,
} from '@local/lib';
import { type StateCreator } from 'zustand';

import type {
  FormulaRow,
  ProductPriceBasisState,
  RangeFormulaRow,
  SingleValueFormulaRow,
  SpecState,
  TemplateSpecConfig,
  ThresholdFormulaRow,
} from '../../qualityFormulaTypes';
import type { QualityFormulaStore } from '../qualityFormulaStore';

import {
  buildDefaultRangeRows,
  buildThresholdSideRows,
  createDefaultPriceBasis,
  createDefaultSpecState,
  createInitialSpecStates,
  makeNewRangeRow,
  makeNewSingleValueRow,
  regenerateRowsForFormulaType,
} from '../../qualityFormulaDefaults';
import { isMultiRowFormulaType, SPEC_TYPES } from '../../qualityFormulaMetadata';

export interface SpecRowEditingSlice {
  applyTemplateToSpec: (specType: SpecType, specConfig: TemplateSpecConfig) => void;
  handleAddRow: (specType: SpecType) => void;
  handleContractedSpecChange: (specType: SpecType, value: number) => void;
  handleFormulaTypeChange: (specType: SpecType, formulaType: QualityFormulaType) => void;
  handlePrecisionChange: (specType: SpecType, value: number) => void;
  handlePriceBasisChange: (specType: SpecType, update: Partial<ProductPriceBasisState>) => void;
  handleRemoveRow: (specType: SpecType, rowIndex: number) => void;
  handleRowChange: (
    specType: SpecType,
    rowIndex: number,
    field: keyof RangeFormulaRow | keyof SingleValueFormulaRow | keyof ThresholdFormulaRow,
    value: boolean | null | number | string,
  ) => void;
  handleThresholdSideToggle: (specType: SpecType, adjustmentMode: AdjustmentMode, isVisible: boolean) => void;
  handleTierModeChange: (specType: SpecType, tierMode: TierMode) => void;
  /** Resets every enabled spec's row fields back to their formula-type defaults — used by `handleResetInputs`. */
  resetSpecValues: () => void;
  specStates: Record<SpecType, SpecState>;
}

export interface CreateSpecRowEditingSliceOptions {
  initialSpecStates?: Record<SpecType, SpecState>;
}

/**
 * Owns editing an already-visible spec's own formula fields — formula type, tier mode, rows,
 * contracted value, precision — plus resetting those fields back to defaults. Never touches which
 * specs are visible/enabled or their collapse state (see `specVisibilitySlice`), even though both
 * slices share the same `specStates` record; Zustand's slices pattern lets either reach it via
 * `get()`/`set()` without one importing the other.
 */
export const createSpecRowEditingSlice =
  (options: CreateSpecRowEditingSliceOptions = {}): StateCreator<QualityFormulaStore, [], [], SpecRowEditingSlice> =>
  (set, get) => ({
    applyTemplateToSpec: (specType, specConfig) => {
      set(state => ({
        specStates: {
          ...state.specStates,
          [specType]: { ...state.specStates[specType], ...specConfig, collapsed: false, enabled: true },
        },
      }));
    },

    handleAddRow: specType => {
      const state = get().specStates[specType];
      // Only ever wired to Range mode's "+ Add range" button — Threshold sides are managed via
      // handleThresholdSideToggle instead, and single-value formula types never have more than
      // one row.
      const rows = [...(state.rows as RangeFormulaRow[])];

      // Only the last row may be open-ended — splitting it gives the previous last row a concrete
      // new ceiling and the freshly appended row picks up as the new open-ended tail.
      const previousLast = rows[rows.length - 1];
      const newBoundary = previousLast.lowerBound + 10;
      rows[rows.length - 1] = { ...previousLast, upperBound: newBoundary };
      // Consecutive ranges most often share the same direction (e.g. two Discount tiers in a row),
      // so the new tail defaults to matching the row it follows instead of resetting to the spec's
      // natural direction — one fewer selection for the common case.
      rows.push({
        ...makeNewRangeRow(specType),
        adjustmentMode: previousLast.adjustmentMode,
        lowerBound: newBoundary,
      });

      set(current => ({ specStates: { ...current.specStates, [specType]: { ...state, rows } } }));
    },

    handleContractedSpecChange: (specType, value) => {
      set(state => ({
        specStates: {
          ...state.specStates,
          [specType]: { ...state.specStates[specType], contractedSpec: value },
        },
      }));
    },

    handleFormulaTypeChange: (specType, formulaType) => {
      const state = get().specStates[specType];
      const isMultiRow = formulaType === QualityFormulaType.Percentage || formulaType === QualityFormulaType.PerUnit;
      let nextRows: FormulaRow[];

      if (!isMultiRow) {
        // A switch to a non-tiered formula type always replaces row 0 outright — only rows beyond
        // the first (if any) are dropped.
        nextRows = [makeNewSingleValueRow(formulaType)];
      } else if (!isMultiRowFormulaType(state.formulaType)) {
        // Coming from a single-value formula type, `state.rows` has no `adjustmentMode`/bounds to
        // carry over (SingleValueFormulaRow doesn't have those fields) — reusing them here would
        // silently produce rows with an undefined `adjustmentMode`, which is what let "Applies to"
        // show nothing selected and dropped the direction arrows/colors. Build a fresh default
        // shape instead, same as a Threshold/Range tier-mode switch would.
        nextRows =
          state.tierMode === TierMode.Threshold
            ? buildThresholdSideRows(specType, [getDefaultAdjustmentModeFromSpecType(specType)], [])
            : buildDefaultRangeRows(specType);
      } else if (state.tierMode === TierMode.Threshold) {
        const existingRows = state.rows as ThresholdFormulaRow[];
        const sides = existingRows.map(row => row.adjustmentMode);
        nextRows = buildThresholdSideRows(specType, sides, existingRows);
      } else {
        nextRows = regenerateRowsForFormulaType(state.rows as RangeFormulaRow[], specType);
      }

      set(current => ({
        specStates: {
          ...current.specStates,
          [specType]: { ...state, formulaType, rows: nextRows },
        },
      }));
    },

    handlePrecisionChange: (specType, value) => {
      set(state => ({
        specStates: { ...state.specStates, [specType]: { ...state.specStates[specType], precision: value } },
      }));
    },

    handlePriceBasisChange: (specType, update) => {
      set(state => ({
        specStates: {
          ...state.specStates,
          [specType]: {
            ...state.specStates[specType],
            priceBasis: { ...state.specStates[specType].priceBasis, ...update },
          },
        },
      }));
    },

    handleRemoveRow: (specType, rowIndex) => {
      const state = get().specStates[specType];
      const minRows = state.tierMode === TierMode.Range ? 2 : 1;
      if (state.rows.length <= minRows) return;

      // Bounds are independently editable now, so removing a row never rewrites its neighbors'
      // values — any resulting gap or overlap just surfaces via getRangeBoundaryIssues/getRangeGapInfo.
      set(current => ({
        specStates: {
          ...current.specStates,
          [specType]: { ...state, rows: state.rows.filter((_, index) => index !== rowIndex) },
        },
      }));
    },

    handleRowChange: (specType, rowIndex, field, value) => {
      // Reads `rows` from the latest state (not a closed-over snapshot) so synchronous repeated
      // calls in the same tick — e.g. a "select all" checkbox looping over every row — each build
      // on the previous call's result instead of all computing off the same stale snapshot and
      // clobbering one another.
      set(current => {
        const { rows } = current.specStates[specType];
        if (rowIndex < 0 || rowIndex >= rows.length) return current;

        // Every bound is freely editable — a new row's lower bound only *defaults* to the previous
        // row's upper bound (see handleAddRow), it's never force-synced afterward.
        const nextRows = rows.map((row, index) => (index === rowIndex ? { ...row, [field]: value } : row));

        return {
          specStates: {
            ...current.specStates,
            [specType]: { ...current.specStates[specType], rows: nextRows },
          },
        };
      });
    },

    handleThresholdSideToggle: (specType, adjustmentMode, isVisible) => {
      const state = get().specStates[specType];
      const existingRows = state.rows as ThresholdFormulaRow[];
      const currentSides = existingRows.map(row => row.adjustmentMode);
      const nextSides = isVisible
        ? [...currentSides, adjustmentMode]
        : currentSides.filter(mode => mode !== adjustmentMode);
      if (nextSides.length === 0) return;

      set(current => ({
        specStates: {
          ...current.specStates,
          [specType]: { ...state, rows: buildThresholdSideRows(specType, nextSides, existingRows) },
        },
      }));
    },

    handleTierModeChange: (specType, tierMode) => {
      const state = get().specStates[specType];
      if (tierMode === state.tierMode) return;

      // Neither direction can reliably reinterpret the other mode's rows, so — like a formula-type
      // switch — it resets to a fresh default shape for the newly selected tier mode. The shared
      // contracted value itself (state.contractedSpec) is the same physical number in either mode,
      // so it's carried over rather than reset.
      const nextRows =
        tierMode === TierMode.Threshold
          ? buildThresholdSideRows(specType, [getDefaultAdjustmentModeFromSpecType(specType)], [])
          : buildDefaultRangeRows(specType);

      set(current => ({
        specStates: { ...current.specStates, [specType]: { ...state, rows: nextRows, tierMode } },
      }));
    },

    resetSpecValues: () => {
      set(state => {
        const specStates = { ...state.specStates };
        SPEC_TYPES.forEach(spec => {
          const specState = specStates[spec.key];
          if (!specState.enabled) return;
          const isMultiRow = isMultiRowFormulaType(specState.formulaType);
          const defaultContractedSpec = createDefaultSpecState(spec.key).contractedSpec;
          let nextRows: FormulaRow[];
          if (!isMultiRow) {
            nextRows = specState.rows.map(() => makeNewSingleValueRow(specState.formulaType));
          } else if (specState.tierMode === TierMode.Threshold) {
            nextRows = buildThresholdSideRows(
              spec.key,
              (specState.rows as ThresholdFormulaRow[]).map(row => row.adjustmentMode),
              [],
            );
          } else {
            nextRows = buildDefaultRangeRows(spec.key);
          }

          specStates[spec.key] = {
            ...specState,
            contractedSpec: isMultiRow ? defaultContractedSpec : specState.contractedSpec,
            precision: createDefaultSpecState(spec.key).precision,
            priceBasis: createDefaultPriceBasis(),
            rows: nextRows,
          };
        });

        return { specStates };
      });
    },

    specStates: options.initialSpecStates ?? createInitialSpecStates(),
  });
